import React, { useState, useMemo } from "react"
import { useLocation } from "react-router-dom"
import { Microphone, Phone, Hash } from "@phosphor-icons/react"
import { cn } from "../../../lib/utils"
import { AgentAvatar } from "../AgentAvatar"
import { useAuthStore } from "../../../stores"
import { useViewingMatterStore } from "../../../stores/viewing-matter-store"
import { DialContactModal } from "../modals/DialContactModal"

interface IdleStateProps {
  collapsed?: boolean
}

export function IdleState({ collapsed }: IdleStateProps) {
  const { user } = useAuthStore()
  const location = useLocation()
  const [showDialModal, setShowDialModal] = useState(false)
  const [dialWithContext, setDialWithContext] = useState(true)
  const viewingMatter = useViewingMatterStore((s) => s.viewingMatter)

  // Extract matter/contact ID for context-aware calling
  // Use viewing matter store for MongoDB ObjectId (URLs now use numeric IDs)
  const { matterId, contactId, hasContext } = useMemo(() => {
    const matterMatch = location.pathname.match(/\/matters\/([^/]+)/)
    const contactMatch = location.pathname.match(/\/contacts\/([^/]+)/)
    // Use MongoDB ObjectId from store for matters (URLs now use numeric IDs)
    const mId = viewingMatter?._id
    const cId = contactMatch?.[1]
    return {
      matterId: mId,
      contactId: cId,
      // hasContext based on URL pattern to show buttons
      hasContext: !!(matterMatch || contactMatch),
    }
  }, [location.pathname, viewingMatter])

  const initials = user
    ? `${user.given_name?.[0] || ""}${user.family_name?.[0] || ""}`.toUpperCase()
    : "NA"

  const displayName = user
    ? `${user.given_name || ""} ${user.family_name || ""}`.trim()
    : "Not Assigned"

  if (collapsed) {
    return (
      <div className="py-3 bg-[#0d1f2d]">
        <div className="flex flex-col items-center">
          <AgentAvatar initials={initials} status="available" size="sm" />
        </div>
      </div>
    )
  }

  return (
    <div className="px-4 py-5 bg-[#0d1f2d]">
      <div className="flex flex-col items-center">
        {/* Avatar with animated ring */}
        <AgentAvatar initials={initials} status="available" size="md" />

        {/* Name */}
        <p className="mt-3 text-[13px] font-semibold text-white">{displayName}</p>

        {/* Status indicator */}
        <div className="mt-2 flex items-center gap-1.5">
          <Microphone className="h-3.5 w-3.5 text-success" />
          <span className="text-[10px] text-success font-medium">Ready</span>
        </div>

        {/* Quick dial buttons - show context button only when on matter/contact page */}
        <div className="mt-4 flex gap-2 w-full">
          {hasContext ? (
            <>
              <button
                onClick={() => {
                  setDialWithContext(true)
                  setShowDialModal(true)
                }}
                className={cn(
                  "flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg",
                  "bg-white/10 hover:bg-white/15 transition-colors",
                  "text-[10px] font-medium text-white/80 hover:text-white"
                )}
              >
                <Phone className="h-3 w-3" />
                <span>New Call</span>
              </button>
              <button
                onClick={() => {
                  setDialWithContext(false)
                  setShowDialModal(true)
                }}
                className={cn(
                  "flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg",
                  "bg-white/10 hover:bg-white/15 transition-colors",
                  "text-[10px] font-medium text-white/80 hover:text-white"
                )}
              >
                <Hash className="h-3 w-3" />
                <span>Number</span>
              </button>
            </>
          ) : (
            <button
              onClick={() => {
                setDialWithContext(false)
                setShowDialModal(true)
              }}
              className={cn(
                "flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg",
                "bg-white/10 hover:bg-white/15 transition-colors",
                "text-[10px] font-medium text-white/80 hover:text-white"
              )}
            >
              <Hash className="h-3 w-3" />
              <span>Number</span>
            </button>
          )}
        </div>
      </div>

      {/* Dial Contact Modal - prefill with context when using "New Call" button */}
      <DialContactModal
        open={showDialModal}
        onOpenChange={setShowDialModal}
        matterId={dialWithContext ? matterId : undefined}
        contactId={dialWithContext ? contactId : undefined}
      />
    </div>
  )
}
